import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useBrand } from "@/lib/brand.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState, Pill } from "@/components/ui-bits";
import { fmtDate } from "@/lib/format";
import { Plus, FileText, Trash2, MapPin, Upload } from "lucide-react";
import { toast } from "sonner";

export default function Templates() {
  const { brands, brandQuery, activeBrandId } = useBrand();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", brand_id: "" });
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/templates", { params: brandQuery });
    setItems(r.data);
  }, [brandQuery]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!file) { toast.error("Pick a PDF template"); return; }
    if (!form.name) { toast.error("Give it a name"); return; }
    if (!form.brand_id) { toast.error("Select a brand"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", form.name);
      fd.append("brand_id", form.brand_id);
      const r = await api.post("/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template uploaded");
      setOpen(false); setFile(null); setForm({ name: "", brand_id: "" });
      nav(`/templates/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete template ${t.name}?`)) return;
    await api.delete(`/templates/${t.id}`); load();
  };

  return (
    <div data-testid="templates-page">
      <PageHeader
        eyebrow="PDF Engine"
        title="Invoice Templates"
        description="Acharya Holdings system templates (tax invoice + proforma) are pre-installed and used automatically by every invoice. Upload your own only if you need a different layout for a specific brand."
        actions={
          <Button onClick={() => { setForm({ name: "", brand_id: activeBrandId !== "all" ? activeBrandId : brands[0]?.id || "" }); setOpen(true); }} className="bg-bronze text-black hover:bg-[#d4af68]" data-testid="upload-template-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Upload template
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState
          title="No templates uploaded"
          description="Drop in your PDF (or proforma) and map fields like {client_name}, {amount}, {gst}, {invoice_number} onto it."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => {
            const brand = brands.find((b) => b.id === t.brand_id);
            const mapped = (t.fields || []).length;
            const isSystem = !!t.is_system;
            return (
              <div key={t.id} className="rounded-xl border border-border bg-card p-5 hover-lift" data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-lg bg-[#1a1a1d] border border-border flex items-center justify-center"><FileText className="w-5 h-5 text-bronze" /></div>
                  {isSystem ? (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-bronze border border-[#c5a059]/40 bg-[#c5a059]/10 px-2 py-0.5 rounded-full font-medium">System</span>
                  ) : (
                    <button onClick={() => del(t)} className="text-muted-foreground hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
                <div className="mt-3 font-medium">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.file_name}</div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  {brand ? <Pill>{brand.code}</Pill> : isSystem ? <Pill>ALL BRANDS</Pill> : null}
                  <span className="text-muted-foreground">{mapped} fields mapped</span>
                </div>
                <div className="mt-4 flex gap-2">
                  <Link to={`/templates/${t.id}`} className="flex-1">
                    <Button variant="outline" className="w-full border-border bg-[#fafaf9]" data-testid={`map-template-${t.id}`}>
                      <MapPin className="w-4 h-4 mr-1.5" /> {isSystem ? "View mapping" : "Map fields"}
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Upload template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Template name</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="bg-white" placeholder="Axera Tax Invoice v1" /></div>
            <div>
              <Label>Brand</Label>
              <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Select brand" /></SelectTrigger>
                <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>PDF file</Label>
              <input ref={fileRef} type="file" accept="application/pdf" onChange={(e)=>setFile(e.target.files?.[0])} className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-[#c5a059] file:text-black file:py-2 file:px-3 file:font-medium" />
              {file && <div className="text-xs text-muted-foreground mt-1"><Upload className="w-3 h-3 inline mr-1" /> {file.name}</div>}
            </div>
            <div className="text-xs text-muted-foreground">v1 supports PDF templates. After upload, map placeholders visually on the next screen.</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-bronze text-black hover:bg-[#d4af68]" data-testid="upload-submit-btn">Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
